import { GetStaticProps, NextPage } from 'next'
import { Stack, Tag, Text } from '@instabox/consumer-web-ui'
import {
  PageProps,
  localeToMarketCode,
  getTranslation,
  fetchMarketById,
  fetchPageLinks,
  fetchPage,
  fetchBanners,
} from '@instabox/consumer-web-shared'
import { useRouter } from 'next/router'
import Fuse from 'fuse.js'
import { FormattedMessage } from 'react-intl'

import ContactUs from '../../components/ContactUs'
import { FAQSearchResult, FAQSearchResults } from '../../components/FAQSearchResults'
import FAQLink from '../../components/FAQLink'
import useFaqsAndCategories from '../../hooks/useFaqsAndCategories'
import FAQSearchContainer from '../../containers/FAQSearchContainer'
import PageContent from '../../components/PageContent'

export interface SearchPageProps extends PageProps {}

const SLUG = 'search'

const SearchPage: NextPage<SearchPageProps> = ({ market, content }) => {
  const router = useRouter()
  const q = String(router.query.q ?? '')
  const { data: { faqs = [], categories = [] } = {} } = useFaqsAndCategories()
  const fuse = new Fuse(faqs, {
    keys: ['translations.question'],
  })
  const searchResults = fuse.search(q)
  const hasSearchResults = searchResults.length > 0

  return (
    <Stack gap="xl">
      <PageContent content={content} slug={SLUG} fields={['titles']} />
      <FAQSearchContainer defaultValue={q} categories={categories} faqs={faqs} />
      <div className="row align-items-start">
        {hasSearchResults && (
          <div className="col-12">
            <Text color="white" fontSize="lg">
              <FormattedMessage id="help.search.showing_results" values={{ query: q }} />
            </Text>
          </div>
        )}
        <div className="col-12 col-md-8">
          <FAQSearchResults>
            {hasSearchResults ? (
              searchResults.map(({ item, refIndex }) => {
                return (
                  <FAQSearchResult key={refIndex}>
                    <Stack gap="sm">
                      <Stack layout="horizontal">
                        {item.categories.map(category => (
                          <Tag key={category.faq_categories_id.id}>
                            {getTranslation(
                              'name',
                              category.faq_categories_id.translations,
                              category.faq_categories_id.name,
                            )}
                          </Tag>
                        ))}
                      </Stack>
                      <FAQLink href={`/help/${item.slug}`}>
                        {getTranslation('question', item.translations, item.question)}
                      </FAQLink>
                    </Stack>
                  </FAQSearchResult>
                )
              })
            ) : (
              <Text fontSize="sm">
                <FormattedMessage id="help.search.no_results" values={{ query: q }} />
              </Text>
            )}
          </FAQSearchResults>
        </div>
        {market && (
          <div className="col-12 col-md-4">
            <ContactUs headingSize="sm" {...market} />
          </div>
        )}
      </div>
    </Stack>
  )
}

export default SearchPage

export const getStaticProps: GetStaticProps<SearchPageProps> = async ({ locale }) => {
  const {
    data: { marketById },
  } = await fetchMarketById(localeToMarketCode(locale))

  const page = await fetchPage(locale, SLUG)
  const pageLinks = await fetchPageLinks(locale)
  const banners = await fetchBanners(locale, SLUG)

  return {
    props: {
      market: marketById,
      pageLinks,
      banners,
      ...page,
    },
    revalidate: 60,
  }
}
